import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useDeviceBases } from "@/features/devices/hooks/use-devices";
import type { Device } from "@/features/devices/types";
import { useApiScope, useClientId } from "@/features/utils/api-scope";
import { useDateFnsLocale, useTranslation } from "@/features/utils/i18n";
import type { ColumnDef } from "@tanstack/react-table";
import { format, formatDistanceToNow } from "date-fns";
import { DeviceAction } from "./columns-action";

/**
 * The registration badge is the column that matters: it is the difference
 * between "we created a row" and "there is a phone on the other end". Anything
 * else on this page can be wrong without the client noticing; this cannot.
 */
const RegistrationBadge = ({ device }: { device: Device }) => {
  const { t } = useTranslation("devices");
  const locale = useDateFnsLocale();

  if (device.status === "suspended")
    return <Badge variant="destructive">{t("devices.status.suspended")}</Badge>;

  if (device.registered)
    return (
      <Badge className="bg-emerald-600 hover:bg-emerald-600">
        {t("devices.status.online")}
      </Badge>
    );

  // Never seen at all reads differently from "was here this morning": one is a
  // handset that was configured wrong, the other is one that got unplugged.
  if (!device.last_registered_at)
    return (
      <Badge variant="outline">{t("devices.status.neverRegistered")}</Badge>
    );

  // "Offline" alone starts a phone call to the client; "offline since Tuesday
  // 09:12" often ends one — that is the day someone moved the router. The exact
  // time is in the tooltip because the relative form is what gets read, and the
  // absolute one is what gets correlated with something else.
  const since = new Date(device.last_registered_at);
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge variant="outline">
            {t("devices.status.offlineSince", {
              since: formatDistanceToNow(since, {
                addSuffix: true,
                locale,
              }),
            })}
          </Badge>
        </TooltipTrigger>
        <TooltipContent>
          {/*
            The stamp is refreshed on a five-minute cron with a fifteen-minute
            write floor, so it trails reality by up to a quarter of an hour.
            Said here rather than left for someone to discover while comparing
            it against a call log.
          */}
          <p>
            {t("devices.status.lastSeenAt", {
              at: format(since, "PPpp", { locale }),
            })}
          </p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

/**
 * The handset table.
 *
 * One set of columns for two audiences: the client's own back-office and the
 * platform's client view, which mount the very same table. The difference is
 * three extra columns under `staff`, and it is a deliberate one — the admin
 * view exists to answer questions about a client's telephony without asking
 * them, so the figures that would otherwise need a dialog per row (what the
 * handset is allowed to spend, which password it holds, what is registered on
 * the other end) are on the row itself.
 *
 * They are extra columns rather than a second table: a client and the person
 * helping them must be looking at the same rows, or the call starts with
 * establishing which screen is right.
 */
export const useDeviceColumns = (): ColumnDef<Device>[] => {
  const { t } = useTranslation("devices");
  const clientId = useClientId();
  const locale = useDateFnsLocale();
  const staff = useApiScope() === "staff";

  // The base is what someone answering "which phone is this, and where is it?"
  // needs, and it is the only part of a base that is not a secret. The codes
  // stay in the dialog.
  const { byDevice } = useDeviceBases(clientId || "");

  return [
    {
      accessorKey: "label",
      header: t("devices.columns.label"),
      cell: ({ row }) => (
        <div className="space-y-0.5">
          <div className="font-medium">{row.original.label}</div>
          <div className="text-muted-foreground font-mono text-xs">
            {row.original.sip_username}
          </div>
        </div>
      ),
    },
    {
      id: "base",
      header: t("devices.columns.base"),
      cell: ({ row }) => {
        const base = byDevice.get(row.original.id!);
        if (!base)
          return <span className="text-muted-foreground text-sm">—</span>;

        return (
          <div className="space-y-0.5">
            <div className="text-sm">{base.label}</div>
            {base.brand && (
              <div className="text-muted-foreground text-xs">{base.brand}</div>
            )}
            {/*
              How many SIP accounts share this box. It is the fact the
              per-handset sheet could not express, and the one that decides
              whether changing a PIN affects one phone or four.
            */}
            {base.devices.length > 1 && (
              <div className="text-muted-foreground text-xs">
                {t("devices.columns.sharedBase", {
                  count: base.devices.length,
                })}
              </div>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: "caller_id",
      header: t("devices.columns.callerId"),
      cell: ({ row }) => (
        <div className="space-y-0.5">
          <span className="font-mono text-sm">{row.original.caller_id}</span>
          {/*
            The number left the channels but the handset still points at it.
            Outbound is already being refused, so this warning is not advisory
            — it is the explanation for calls that are failing right now.
          */}
          {!row.original.caller_id_owned && (
            <div className="text-destructive text-xs">
              {t("devices.status.callerIdRevoked")}
            </div>
          )}
        </div>
      ),
    },
    {
      id: "capabilities",
      header: t("devices.columns.capabilities"),
      cell: ({ row }) => (
        <div className="flex gap-1">
          {row.original.inbound_enabled && (
            <Badge variant="secondary">{t("devices.columns.inbound")}</Badge>
          )}
          {row.original.outbound_enabled && (
            <Badge variant="secondary">{t("devices.columns.outbound")}</Badge>
          )}
        </div>
      ),
    },
    {
      id: "usage",
      header: t("devices.columns.usage"),
      cell: ({ row }) => {
        const used = row.original.minutes_this_month || 0;
        const cap = row.original.monthly_minutes_cap || 0;
        // A cap the client sets but never sees the consumption of is half a
        // feature: without this column the first sign the month is spent is a
        // call that does not go through. Shown in red from 90% so there is
        // somewhere to act before that happens.
        const spent = cap > 0 && used >= cap * 0.9;
        return (
          <div className={spent ? "text-destructive text-sm" : "text-sm"}>
            {cap > 0
              ? t("devices.usage.withCap", { used, cap })
              : t("devices.usage.noCap", { used })}
          </div>
        );
      },
    },
    {
      id: "registration",
      header: t("devices.columns.state"),
      cell: ({ row }) => (
        <div className="space-y-0.5">
          <RegistrationBadge device={row.original} />
          {/*
            What is actually on the other end. "Registered" plus a user agent
            answers "did they plug in the phone we think they did?" — the
            question a support call opens with — and it is already in the
            payload.
          */}
          {staff && row.original.registration?.user_agent && (
            <div className="text-muted-foreground max-w-[14rem] truncate text-xs">
              {row.original.registration.user_agent}
            </div>
          )}
        </div>
      ),
    },
    // ── Staff-only, below ────────────────────────────────────────────────
    ...(staff
      ? ([
          {
            id: "limits",
            header: t("devices.columns.limits"),
            cell: ({ row }) => {
              const ceiling = row.original.max_prefixes || [];
              const chosen = row.original.allowed_prefixes || [];
              const seconds = row.original.max_call_seconds || 0;

              return (
                <div className="space-y-0.5 text-xs">
                  <div>
                    <span className="text-muted-foreground">
                      {t("devices.columns.ceiling")}{" "}
                    </span>
                    <span className="font-mono">
                      {ceiling.length
                        ? ceiling.join(" ")
                        : t("devices.columns.callerIdCountry")}
                    </span>
                  </div>
                  {chosen.length > 0 && (
                    <div>
                      <span className="text-muted-foreground">
                        {t("devices.columns.clientChoice")}{" "}
                      </span>
                      <span className="font-mono">{chosen.join(" ")}</span>
                    </div>
                  )}
                  <div className="text-muted-foreground">
                    {seconds > 0
                      ? t("devices.columns.maxCall", { seconds })
                      : t("devices.columns.maxCallDefault")}
                  </div>
                </div>
              );
            },
          },
          {
            id: "password",
            header: t("devices.columns.password"),
            cell: ({ row }) =>
              row.original.sip_password_hint ? (
                <span className="font-mono text-xs">
                  {row.original.sip_password_hint}
                  <span className="text-muted-foreground">••••••••</span>
                </span>
              ) : (
                <span className="text-muted-foreground text-xs">
                  {t("devices.columns.passwordUnknown")}
                </span>
              ),
          },
          {
            id: "created",
            header: t("devices.columns.created"),
            cell: ({ row }) =>
              row.original.created_at ? (
                <span className="text-muted-foreground text-xs">
                  {formatDistanceToNow(new Date(row.original.created_at), {
                    addSuffix: true,
                    locale,
                  })}
                </span>
              ) : (
                <span className="text-muted-foreground text-xs">—</span>
              ),
          },
        ] as ColumnDef<Device>[])
      : []),
    {
      id: "actions",
      header: () => <div className="text-right">{t("devices.columns.actions")}</div>,
      cell: ({ row }) => <DeviceAction device={row.original} />,
    },
  ];
};
